import type {
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import { buildAnnualCellKey } from '../utils/annualTimetable';
import { auditLearningBodySnippet } from '../utils/programLearningContentFields';
import {
  isPersistableUuid,
  resolvePresentationRefIdFromLearningContent,
} from '../utils/programContentSlideOwner';
import { supabase } from '../supabaseClient';
import { getProgramFixedContentRows } from './programFixedContentService';
import { deleteProgramProgressCellsByIds, getProgramProgressCells } from './programProgressService';
import {
  deleteProgramContentSlidePagesForOwner,
  invalidateProgramContentSlidePresenceCache,
} from './programContentSlideService';
import { getProgramScopedContentRowsForGradeTerm } from './programScopedContentService';
import { findEmptyPresentationBindingsForScope } from './programEmptyPresentationBindingService';
import {
  loadAnnualPresentationAuditSnapshotCache,
  saveAnnualPresentationAuditSnapshotCache,
} from '../utils/presentationAuditSnapshotCache';
import {
  presentationRefSlideLinkStateLabel,
  resolvePresentationRefSlideLinkState,
  type PresentationRefSlideLinkState,
} from '../utils/programPresentationLinkState';
import {
  fetchWorksheetAuditEntriesForAnnualProgress,
  type WorksheetAuditEntry,
} from './programWorksheetAuditService';

export type PresentationAuditLinkKind =
  | 'linked'
  | 'linked_hidden_legacy_cell'
  | 'orphan_unreferenced_ref'
  | 'orphan_owner_missing';

export type PresentationAuditEntry = {
  selectionKey: string;
  ownerKind: ProgramContentSlideOwnerRef['kind'];
  ownerId: string;
  pageCount: number;
  pageIndexSummary: string;
  lastUpdatedAt: string | null;
  linkKind: PresentationAuditLinkKind;
  linkLabel: string;
  linkState: PresentationRefSlideLinkState | null;
  contextLabel: string;
  learningSnippet: string;
};

export type AnnualProgressAuditSnapshot = {
  programProgressCells: ProgramProgressCell[];
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
  presentationEntries: PresentationAuditEntry[];
  worksheetEntries: WorksheetAuditEntry[];
};

export type ProgramPresentationAuditParams = {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  programProgressCells: ProgramProgressCell[];
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
};

const SLIDE_PAGE_SELECT =
  'id, workspace_id, page_index, updated_at, program_progress_cell_id, program_fixed_content_row_id, program_scoped_content_row_id, presentation_ref_id';

type SlidePageRow = {
  page_index: number;
  updated_at: string | null;
  program_progress_cell_id: string | null;
  program_fixed_content_row_id: string | null;
  program_scoped_content_row_id: string | null;
  presentation_ref_id: string | null;
};

type SlideOwnerAgg = {
  ownerKind: ProgramContentSlideOwnerRef['kind'];
  ownerId: string;
  pageCount: number;
  minPage: number;
  maxPage: number;
  lastUpdatedAt: string | null;
};

function chunkIds<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function collectReferencedPresentationRefIds(
  cells: ProgramProgressCell[],
  scopedRows: ProgramScopedContentRow[],
  fixedRows: ProgramFixedContentRow[]
): Set<string> {
  const out = new Set<string>();
  const absorb = (row: ProgramProgressCell | ProgramScopedContentRow | ProgramFixedContentRow) => {
    const ref = resolvePresentationRefIdFromLearningContent(row);
    if (ref) out.add(ref);
  };
  cells.forEach((c) => absorb(c));
  scopedRows.forEach((r) => absorb(r));
  fixedRows.forEach((r) => absorb(r));
  return out;
}

function upsertSlideAgg(map: Map<string, SlideOwnerAgg>, kind: ProgramContentSlideOwnerRef['kind'], id: string, pageIndex: number, updatedAt: string | null) {
  const oid = id.trim();
  if (!oid || !isPersistableUuid(oid)) return;
  const key = `${kind}:${oid}`;
  const prev = map.get(key);
  if (!prev) {
    map.set(key, {
      ownerKind: kind,
      ownerId: oid,
      pageCount: 1,
      minPage: pageIndex,
      maxPage: pageIndex,
      lastUpdatedAt: updatedAt,
    });
    return;
  }
  prev.pageCount += 1;
  prev.minPage = Math.min(prev.minPage, pageIndex);
  prev.maxPage = Math.max(prev.maxPage, pageIndex);
  const nextUpdated = (updatedAt ?? '').trim();
  if (nextUpdated && (!prev.lastUpdatedAt || nextUpdated > prev.lastUpdatedAt)) {
    prev.lastUpdatedAt = nextUpdated;
  }
}

async function fetchSlidePagesByColumn(
  workspaceId: string,
  column: 'program_progress_cell_id' | 'program_fixed_content_row_id' | 'program_scoped_content_row_id' | 'presentation_ref_id',
  ids: string[]
): Promise<SlidePageRow[]> {
  const sid = workspaceId.trim();
  if (!sid || !ids.length) return [];
  const out: SlidePageRow[] = [];
  for (const batch of chunkIds(ids, 40)) {
    const { data, error } = await supabase
      .from('program_content_slide_pages')
      .select(SLIDE_PAGE_SELECT)
      .eq('workspace_id', sid)
      .in(column, batch);
    if (error) throw new Error(error.message);
    out.push(...(((data ?? []) as unknown) as SlidePageRow[]));
  }
  return out;
}

async function fetchAllPresentationRefSlidePages(workspaceId: string): Promise<SlidePageRow[]> {
  const sid = workspaceId.trim();
  if (!sid) return [];
  const { data, error } = await supabase
    .from('program_content_slide_pages')
    .select(SLIDE_PAGE_SELECT)
    .eq('workspace_id', sid)
    .not('presentation_ref_id', 'is', null);
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown) as SlidePageRow[];
}

function aggregateSlidePages(rows: SlidePageRow[]): Map<string, SlideOwnerAgg> {
  const map = new Map<string, SlideOwnerAgg>();
  rows.forEach((row) => {
    const pi = Number(row.page_index);
    if (!Number.isFinite(pi) || pi < 0) return;
    const updatedAt = row.updated_at ?? null;
    if (row.program_progress_cell_id) {
      upsertSlideAgg(map, 'program_cell', row.program_progress_cell_id, pi, updatedAt);
    }
    if (row.program_scoped_content_row_id) {
      upsertSlideAgg(map, 'scoped_row', row.program_scoped_content_row_id, pi, updatedAt);
    }
    if (row.program_fixed_content_row_id) {
      upsertSlideAgg(map, 'fixed_row', row.program_fixed_content_row_id, pi, updatedAt);
    }
    if (row.presentation_ref_id) {
      upsertSlideAgg(map, 'presentation_ref', row.presentation_ref_id, pi, updatedAt);
    }
  });
  return map;
}

function learningSnippet(row: ProgramProgressCell | ProgramScopedContentRow | ProgramFixedContentRow): string {
  return auditLearningBodySnippet(row);
}

function cellContextLabel(cell: ProgramProgressCell | undefined): string {
  if (!cell) return '—';
  return `${cell.monday_iso} ${cell.weekday_key} #${cell.slot_index}`;
}

function classifyPresentationEntry(params: {
  agg: SlideOwnerAgg;
  referencedRefs: Set<string>;
  cellById: Map<string, ProgramProgressCell>;
  scopedById: Map<string, ProgramScopedContentRow>;
  fixedById: Map<string, ProgramFixedContentRow>;
  scopedBySourceAnnualKey: Map<string, ProgramScopedContentRow[]>;
}): Pick<PresentationAuditEntry, 'linkKind' | 'linkLabel' | 'linkState' | 'contextLabel' | 'learningSnippet'> {
  const { agg, referencedRefs, cellById, scopedById, fixedById, scopedBySourceAnnualKey } = params;
  const { ownerKind, ownerId } = agg;

  if (ownerKind === 'presentation_ref') {
    const hasRef = referencedRefs.has(ownerId);
    const linkState = resolvePresentationRefSlideLinkState({
      hasRef,
      slidePageCount: agg.pageCount,
    });
    if (hasRef) {
      return {
        linkKind: 'linked',
        linkLabel: presentationRefSlideLinkStateLabel(linkState),
        linkState,
        contextLabel: `presentation_ref ${ownerId.slice(0, 8)}…`,
        learningSnippet: '—',
      };
    }
    return {
      linkKind: 'orphan_unreferenced_ref',
      linkLabel: presentationRefSlideLinkStateLabel(linkState) || '고아 — presentation_ref_id 없음',
      linkState,
      contextLabel: `presentation_ref ${ownerId.slice(0, 8)}…`,
      learningSnippet: '—',
    };
  }

  if (ownerKind === 'program_cell') {
    const cell = cellById.get(ownerId);
    if (!cell) {
      return {
        linkKind: 'orphan_owner_missing',
        linkLabel: '고아 — 프로그램 진도셀 행 없음',
        linkState: resolvePresentationRefSlideLinkState({ hasRef: false, slidePageCount: agg.pageCount }),
        contextLabel: ownerId.slice(0, 8) + '…',
        learningSnippet: '—',
      };
    }
    const annualKey = buildAnnualCellKey(cell.grade_id, cell.monday_iso, cell.weekday_key, cell.slot_index);
    const refOnCell = resolvePresentationRefIdFromLearningContent(cell);
    const scopedCandidates = scopedBySourceAnnualKey.get(annualKey.trim()) ?? [];
    const refOnScoped = scopedCandidates
      .map((r) => resolvePresentationRefIdFromLearningContent(r))
      .find(Boolean);
    const hasRef = !!(refOnCell || refOnScoped);
    const linkState = resolvePresentationRefSlideLinkState({ hasRef, slidePageCount: agg.pageCount });
    if (refOnCell || refOnScoped) {
      return {
        linkKind: 'linked',
        linkLabel: presentationRefSlideLinkStateLabel(linkState),
        linkState,
        contextLabel: cellContextLabel(cell),
        learningSnippet: learningSnippet(cell),
      };
    }
    if (scopedCandidates.length > 0) {
      return {
        linkKind: 'linked_hidden_legacy_cell',
        linkLabel: '숨김 데이터 — 레거시 프로그램 진도셀에만 슬라이드(UI 미노출)',
        linkState,
        contextLabel: cellContextLabel(cell),
        learningSnippet: learningSnippet(cell),
      };
    }
    return {
      linkKind: 'linked',
      linkLabel: presentationRefSlideLinkStateLabel(linkState) || '진도셀 단독 저장',
      linkState,
      contextLabel: cellContextLabel(cell),
      learningSnippet: learningSnippet(cell),
    };
  }

  if (ownerKind === 'scoped_row') {
    const row = scopedById.get(ownerId);
    if (!row) {
      return {
        linkKind: 'orphan_owner_missing',
        linkLabel: '고아 — scoped_row 없음',
        linkState: resolvePresentationRefSlideLinkState({ hasRef: false, slidePageCount: agg.pageCount }),
        contextLabel: ownerId.slice(0, 8) + '…',
        learningSnippet: '—',
      };
    }
    const hasRef = !!resolvePresentationRefIdFromLearningContent(row);
    const linkState = resolvePresentationRefSlideLinkState({ hasRef, slidePageCount: agg.pageCount });
    return {
      linkKind: 'linked',
      linkLabel: presentationRefSlideLinkStateLabel(linkState),
      linkState,
      contextLabel: `${row.scope_type}|${row.scope_key} #${row.order_index}`,
      learningSnippet: learningSnippet(row),
    };
  }

  const row = fixedById.get(ownerId);
  if (!row) {
    return {
      linkKind: 'orphan_owner_missing',
      linkLabel: '고아 — fixed_row 없음',
      linkState: resolvePresentationRefSlideLinkState({ hasRef: false, slidePageCount: agg.pageCount }),
      contextLabel: ownerId.slice(0, 8) + '…',
      learningSnippet: '—',
    };
  }
  const hasRef = !!resolvePresentationRefIdFromLearningContent(row);
  const linkState = resolvePresentationRefSlideLinkState({ hasRef, slidePageCount: agg.pageCount });
  return {
    linkKind: 'linked',
    linkLabel: presentationRefSlideLinkStateLabel(linkState),
    linkState,
    contextLabel: `order #${row.order_index}${row.source_annual_cell_key ? ` · ${row.source_annual_cell_key}` : ''}`,
    learningSnippet: learningSnippet(row),
  };
}

async function buildPresentationAuditEntriesForScope(
  params: ProgramPresentationAuditBuildParams & { workspaceId: string }
): Promise<PresentationAuditEntry[]> {
  const slideEntries = buildPresentationAuditEntries(params);
  const emptyBindings = await findEmptyPresentationBindingsForScope({
    workspaceId: params.workspaceId,
    scopedRows: params.scopedRows,
    fixedRows: params.fixedRows,
    programProgressCells: params.programProgressCells,
  });
  const seen = new Set(slideEntries.map((entry) => entry.selectionKey));
  const merged = [...slideEntries];
  emptyBindings.forEach((binding) => {
    if (seen.has(binding.selectionKey)) return;
    seen.add(binding.selectionKey);
    merged.push({
      selectionKey: binding.selectionKey,
      ownerKind: 'presentation_ref',
      ownerId: binding.presentationRefId,
      pageCount: 0,
      pageIndexSummary: '—',
      lastUpdatedAt: null,
      linkKind: 'linked',
      linkState: 'ref_no_slides',
      linkLabel: presentationRefSlideLinkStateLabel('ref_no_slides'),
      contextLabel: binding.contextLabel,
      learningSnippet: binding.learningSnippet,
    });
  });
  return merged.sort((a, b) => {
    const rank = (state: PresentationRefSlideLinkState | null) => {
      if (state === 'ref_no_slides' || state === 'slides_no_ref') return 0;
      return 1;
    };
    const d = rank(a.linkState) - rank(b.linkState);
    if (d !== 0) return d;
    return a.selectionKey.localeCompare(b.selectionKey, 'en');
  });
}

function buildPresentationAuditEntries(params: ProgramPresentationAuditBuildParams): PresentationAuditEntry[] {
  const { programProgressCells, scopedRows, fixedRows, presentationAggs, referencedRefs } = params;
  const cellById = new Map(programProgressCells.map((c) => [c.id, c] as const));
  const scopedById = new Map(scopedRows.map((r) => [r.id, r] as const));
  const fixedById = new Map(fixedRows.map((r) => [r.id, r] as const));
  const scopedBySourceAnnualKey = new Map<string, ProgramScopedContentRow[]>();
  scopedRows.forEach((r) => {
    const key = (r.source_annual_cell_key ?? '').trim();
    if (!key) return;
    const list = scopedBySourceAnnualKey.get(key) ?? [];
    list.push(r);
    scopedBySourceAnnualKey.set(key, list);
  });

  return params.presentationAggs
    .sort((a, b) => {
      const rank = (k: PresentationAuditLinkKind) =>
        k === 'orphan_unreferenced_ref' || k === 'orphan_owner_missing' ? 0 : 1;
      const aKind = classifyPresentationEntry({
        agg: a,
        referencedRefs: params.referencedRefs,
        cellById,
        scopedById,
        fixedById,
        scopedBySourceAnnualKey,
      }).linkKind;
      const bKind = classifyPresentationEntry({
        agg: b,
        referencedRefs: params.referencedRefs,
        cellById,
        scopedById,
        fixedById,
        scopedBySourceAnnualKey,
      }).linkKind;
      const d = rank(aKind) - rank(bKind);
      if (d !== 0) return d;
      return `${a.ownerKind}:${a.ownerId}`.localeCompare(`${b.ownerKind}:${b.ownerId}`);
    })
    .map((agg) => {
      const meta = classifyPresentationEntry({
        agg,
        referencedRefs: params.referencedRefs,
        cellById,
        scopedById,
        fixedById,
        scopedBySourceAnnualKey,
      });
      const pageIndexSummary =
        agg.minPage === agg.maxPage ? String(agg.minPage) : `${agg.minPage}–${agg.maxPage}`;
      return {
        selectionKey: `slide:${agg.ownerKind}:${agg.ownerId}`,
        ownerKind: agg.ownerKind,
        ownerId: agg.ownerId,
        pageCount: agg.pageCount,
        pageIndexSummary,
        lastUpdatedAt: agg.lastUpdatedAt,
        ...meta,
      };
    });
}

type ProgramPresentationAuditBuildParams = ProgramPresentationAuditParams & {
  presentationAggs: SlideOwnerAgg[];
  referencedRefs: Set<string>;
};

async function loadPresentationSlideAggregates(params: {
  workspaceId: string;
  programProgressCells: ProgramProgressCell[];
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
  referencedRefs: Set<string>;
  /** false면 워크스페이스 전역 presentation_ref 고아 스캔 생략(다른 편제·고정 프로그램 슬라이드 오탐 방지) */
  includeWorkspaceOrphanRefs?: boolean;
}): Promise<SlideOwnerAgg[]> {
  const cellIds = params.programProgressCells.map((c) => c.id).filter((id) => isPersistableUuid(id));
  const scopedIds = params.scopedRows.map((r) => r.id).filter((id) => isPersistableUuid(id));
  const fixedIds = params.fixedRows.map((r) => r.id).filter((id) => isPersistableUuid(id));
  const refIds = Array.from(params.referencedRefs);

  const pageRows: SlidePageRow[] = [];
  pageRows.push(...(await fetchSlidePagesByColumn(params.workspaceId, 'program_progress_cell_id', cellIds)));
  pageRows.push(...(await fetchSlidePagesByColumn(params.workspaceId, 'program_scoped_content_row_id', scopedIds)));
  pageRows.push(...(await fetchSlidePagesByColumn(params.workspaceId, 'program_fixed_content_row_id', fixedIds)));
  pageRows.push(...(await fetchSlidePagesByColumn(params.workspaceId, 'presentation_ref_id', refIds)));

  if (params.includeWorkspaceOrphanRefs) {
    const allRefPages = await fetchAllPresentationRefSlidePages(params.workspaceId);
    const orphanRefPages = allRefPages.filter((row) => {
      const ref = (row.presentation_ref_id ?? '').trim();
      return ref && !params.referencedRefs.has(ref);
    });
    pageRows.push(...orphanRefPages);
  }

  return Array.from(aggregateSlidePages(pageRows).values());
}

export async function fetchAnnualProgressAuditSnapshot(
  params: {
    workspaceId: string;
    academicYear: number;
    termNumber: 1 | 2;
    gradeId: string;
  },
  options?: { forceRefresh?: boolean }
): Promise<AnnualProgressAuditSnapshot> {
  const { workspaceId, academicYear, termNumber, gradeId } = params;
  if (!workspaceId.trim() || !gradeId.trim()) {
    return {
      programProgressCells: [],
      scopedRows: [],
      fixedRows: [],
      presentationEntries: [],
      worksheetEntries: [],
    };
  }

  const loadRows = () =>
    Promise.all([
      getProgramProgressCells(workspaceId, academicYear, termNumber, gradeId, { forceRefresh: false }),
      getProgramScopedContentRowsForGradeTerm(workspaceId, academicYear, termNumber, gradeId, {
        forceRefresh: false,
      }),
      getProgramFixedContentRows(workspaceId, academicYear, termNumber, gradeId, { forceRefresh: false }),
    ]);

  if (!options?.forceRefresh) {
    const cached = loadAnnualPresentationAuditSnapshotCache(
      workspaceId,
      academicYear,
      termNumber,
      gradeId
    );
    if (cached) {
      const [programProgressCells, scopedRows, fixedRows] = await loadRows();
      const worksheetEntries = await fetchWorksheetAuditEntriesForAnnualProgress({
        workspaceId,
        programProgressCells,
        scopedRows,
        fixedRows,
      });
      return {
        programProgressCells,
        scopedRows,
        fixedRows,
        presentationEntries: cached.presentationEntries,
        worksheetEntries,
      };
    }
  }

  const [programProgressCells, scopedRows, fixedRows] = await loadRows();

  const referencedRefs = collectReferencedPresentationRefIds(programProgressCells, scopedRows, fixedRows);
  const presentationAggs = await loadPresentationSlideAggregates({
    workspaceId,
    programProgressCells,
    scopedRows,
    fixedRows,
    referencedRefs,
  });

  const [presentationEntries, worksheetEntries] = await Promise.all([
    buildPresentationAuditEntriesForScope({
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      programProgressCells,
      scopedRows,
      fixedRows,
      presentationAggs,
      referencedRefs,
    }),
    fetchWorksheetAuditEntriesForAnnualProgress({
      workspaceId,
      programProgressCells,
      scopedRows,
      fixedRows,
    }),
  ]);

  saveAnnualPresentationAuditSnapshotCache(workspaceId, academicYear, termNumber, gradeId, {
    presentationEntries,
    scopedRowIds: scopedRows.map((row) => row.id.trim()).filter(Boolean),
    fixedRowIds: fixedRows.map((row) => row.id.trim()).filter(Boolean),
    programCellIds: programProgressCells.map((cell) => cell.id.trim()).filter(Boolean),
  });

  return { programProgressCells, scopedRows, fixedRows, presentationEntries, worksheetEntries };
}

export async function fetchPresentationAuditEntriesForProgramScope(
  params: ProgramPresentationAuditParams
): Promise<PresentationAuditEntry[]> {
  const referencedRefs = collectReferencedPresentationRefIds(
    params.programProgressCells,
    params.scopedRows,
    params.fixedRows
  );
  const presentationAggs = await loadPresentationSlideAggregates({
    workspaceId: params.workspaceId,
    programProgressCells: params.programProgressCells,
    scopedRows: params.scopedRows,
    fixedRows: params.fixedRows,
    referencedRefs,
  });
  return buildPresentationAuditEntriesForScope({
    ...params,
    presentationAggs,
    referencedRefs,
  });
}

export function parsePresentationAuditSelectionKey(key: string): ProgramContentSlideOwnerRef | null {
  if (!key.startsWith('slide:')) return null;
  const parts = key.split(':');
  if (parts.length !== 3) return null;
  const kind = parts[1] as ProgramContentSlideOwnerRef['kind'];
  const id = parts[2]?.trim() ?? '';
  if (!id || !isPersistableUuid(id)) return null;
  if (kind !== 'program_cell' && kind !== 'scoped_row' && kind !== 'fixed_row' && kind !== 'presentation_ref') {
    return null;
  }
  return { kind, id };
}

export async function deletePresentationAuditSelections(params: {
  workspaceId: string;
  selectionKeys: string[];
}): Promise<void> {
  const sid = params.workspaceId.trim();
  if (!sid) return;
  const owners: ProgramContentSlideOwnerRef[] = [];
  const seen = new Set<string>();
  params.selectionKeys.forEach((key) => {
    const owner = parsePresentationAuditSelectionKey(key);
    if (!owner) return;
    const k = `${owner.kind}:${owner.id}`;
    if (seen.has(k)) return;
    seen.add(k);
    owners.push(owner);
  });
  for (const owner of owners) {
    await deleteProgramContentSlidePagesForOwner(sid, owner);
  }
  invalidateProgramContentSlidePresenceCache(sid);
}

export async function deleteProgramProgressCellsAuditSelections(params: {
  workspaceId: string;
  cellIds: string[];
}): Promise<void> {
  const sid = params.workspaceId.trim();
  const ids = params.cellIds.map((v) => v.trim()).filter((id) => isPersistableUuid(id));
  if (!sid || !ids.length) return;
  for (const id of ids) {
    await deleteProgramContentSlidePagesForOwner(sid, { kind: 'program_cell', id });
  }
  await deleteProgramProgressCellsByIds(ids);
  invalidateProgramContentSlidePresenceCache(sid);
}
